import { useGqlMutation, useGqlQuery } from "@/lib/GraphQLCodegen/fetcher";
import { useRelocationRecord } from "@/app/Relocations/Hooks/useRelocationRecord";
import { ImoovaImage } from "@/app/Media/Components/Image";
import { Button } from "@/components/catalyst/button";
import { CheckIcon, ImageIcon } from "lucide-react";
import {
  relocationImageGenerations,
  setActiveImageGenerationMutation,
} from "@/app/Relocations/GraphQL/relocationImageGenerations";
import { ReactNode } from "react";

export function RelocationImageGenerationList({
  generatingPlaceholder,
}: {
  generatingPlaceholder?: ReactNode;
}) {
  const relocation = useRelocationRecord();
  const { data } = useGqlQuery(relocationImageGenerations, {
    id: relocation.id,
  });

  const { mutateAsync: setDefault, isPending: isSettingDefault } =
    useGqlMutation(setActiveImageGenerationMutation);

  const hasGenerations = !!data?.relocationImageGenerations?.length;

  if (!hasGenerations && !generatingPlaceholder) {
    return (
      <div className="flex flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 p-12 text-center">
        <ImageIcon className="mb-4 h-12 w-12 text-gray-400" />
        <p className="text-sm font-medium text-gray-900">
          No generated images yet
        </p>
        <p className="mt-1 text-sm text-gray-500">
          Images will appear here once generated
        </p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
      {generatingPlaceholder}
      {data?.relocationImageGenerations?.map((generation) => {
        return (
          <div
            key={generation.id}
            className={`relative overflow-hidden rounded-lg border-2 ${
              generation.active
                ? "border-green-500 bg-green-50"
                : "border-gray-200 hover:border-gray-300"
            }`}
          >
            <ImoovaImage
              alt="Generated OG Image"
              className="w-full object-contain"
              media={generation.image}
            />
            <div className="p-3">
              <div className="flex flex-col gap-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500">
                    {new Date(generation.created_at).toLocaleDateString()}
                  </span>
                  {generation.active ? (
                    <div className="flex items-center gap-1 text-xs font-medium text-green-600">
                      <CheckIcon className="h-3 w-3" />
                      Active
                    </div>
                  ) : null}
                </div>
                {!generation.active ? (
                  <Button
                    color="green"
                    isLoading={isSettingDefault}
                    onClick={async () => {
                      return setDefault({
                        id: generation.id,
                      });
                    }}
                  >
                    Set as Active
                  </Button>
                ) : null}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
